import { UzButton, UzCard } from '@utazzo/ui';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { formatDateDMY, formatInrPaise } from '../invoices.helpers';
import { useSupplierPaymentSchedule } from '../use-invoices';

import type { SupplierPaymentScheduleBucket } from '@utazzo/shared';

import { PageShell } from '@/components/page-shell';
import { SupplierPicker } from '@/components/pickers/SupplierPicker';

/**
 * Supplier payment schedule (route /reports/supplier-payment-schedule) - the
 * working-capital view. Every PLANNED supplier installment term across
 * invoices, aged into due-date buckets (overdue / this week / this month /
 * later / unscheduled) so upcoming cash outflows are visible at a glance. Each
 * row shows the term amount, what settlement has already covered it (FIFO) and
 * what is still to pay.
 */

const BUCKET_META: Record<
  SupplierPaymentScheduleBucket,
  { label: string; cls: string; accent: string }
> = {
  OVERDUE: { label: 'Overdue', cls: 'bg-red-50 text-red-700', accent: 'text-red-600' },
  THIS_WEEK: { label: 'This week', cls: 'bg-amber-50 text-amber-700', accent: 'text-amber-600' },
  THIS_MONTH: { label: 'This month', cls: 'bg-sky-50 text-sky-700', accent: 'text-sky-600' },
  LATER: { label: 'Later', cls: 'bg-zinc-100 text-zinc-600', accent: 'text-zinc-700' },
  UNSCHEDULED: {
    label: 'Unscheduled',
    cls: 'bg-zinc-100 text-zinc-500',
    accent: 'text-zinc-500',
  },
};

const BUCKET_ORDER: SupplierPaymentScheduleBucket[] = [
  'OVERDUE',
  'THIS_WEEK',
  'THIS_MONTH',
  'LATER',
  'UNSCHEDULED',
];

export const SupplierPaymentSchedulePage = (): React.JSX.Element => {
  const [supplierId, setSupplierId] = useState<string>('');
  const [pendingOnly, setPendingOnly] = useState<boolean>(true);

  const scheduleQuery = useSupplierPaymentSchedule({
    ...(supplierId ? { supplierId } : {}),
    ...(pendingOnly ? { pendingOnly: true } : {}),
  });

  const rows = scheduleQuery.data?.rows ?? [];
  const byBucket = scheduleQuery.data?.byBucket ?? [];

  return (
    <PageShell
      title="Supplier payment schedule"
      description="Planned supplier installments aged by due date - your upcoming working-capital outflows."
    >
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-64">
          <SupplierPicker
            label="Supplier (all if blank)"
            value={supplierId || null}
            onValueChange={(v) => setSupplierId(v ?? '')}
          />
        </div>
        <label className="mb-1.5 inline-flex cursor-pointer items-center gap-2 text-sm text-zinc-600">
          <input
            type="checkbox"
            checked={pendingOnly}
            onChange={(e) => setPendingOnly(e.target.checked)}
            className="h-4 w-4 rounded border-zinc-300"
          />
          Pending only
        </label>
        <UzButton variant="outline" onClick={() => void scheduleQuery.refetch()}>
          Refresh
        </UzButton>
      </div>

      {scheduleQuery.data && rows.length > 0 && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {BUCKET_ORDER.map((bucket) => {
            const total = byBucket.find((b) => b.bucket === bucket);
            const meta = BUCKET_META[bucket];
            return (
              <UzCard key={bucket} className="min-w-0">
                <p className="text-xs font-medium text-zinc-500">{meta.label}</p>
                <p className={`mt-1 text-lg font-semibold tabular-nums ${meta.accent}`}>
                  {formatInrPaise(total?.outstandingPaise ?? '0')}
                </p>
              </UzCard>
            );
          })}
        </div>
      )}

      <UzCard padding="none">
        {scheduleQuery.isLoading ? (
          <p className="p-6 text-sm text-zinc-500">Loading schedule...</p>
        ) : scheduleQuery.isError ? (
          <p className="p-6 text-sm text-red-600">Could not load the schedule.</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-zinc-500">
            No planned installments{pendingOnly ? ' still pending' : ''}. Plan a supplier line from
            its invoice to see it here.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
                  <th className="px-4 py-3">Due</th>
                  <th className="px-4 py-3">Bucket</th>
                  <th className="px-4 py-3">Supplier</th>
                  <th className="px-4 py-3">Invoice no.</th>
                  <th className="px-4 py-3">Term</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3 text-right">Paid</th>
                  <th className="px-4 py-3 text-right">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const meta = BUCKET_META[row.bucket];
                  return (
                    <tr key={row.termId} className="border-b border-zinc-100 last:border-0">
                      <td className="px-4 py-3 whitespace-nowrap text-zinc-700 tabular-nums">
                        {row.dueDate ? formatDateDMY(row.dueDate) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${meta.cls}`}
                        >
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-700">{row.supplierName ?? '-'}</td>
                      <td className="px-4 py-3 whitespace-nowrap tabular-nums">
                        {row.invoiceNumber ? (
                          <Link
                            to={`/invoices/${row.invoiceId}`}
                            className="text-(--primary) hover:underline"
                          >
                            {row.invoiceNumber}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-zinc-600 tabular-nums">
                        #{row.termNo}
                        {row.description ? ` · ${row.description}` : ''}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap text-zinc-900 tabular-nums">
                        {formatInrPaise(row.amountPaise)}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap text-zinc-700 tabular-nums">
                        {formatInrPaise(row.paidPaise)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium whitespace-nowrap text-zinc-900 tabular-nums">
                        {formatInrPaise(row.outstandingPaise)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </UzCard>

      {scheduleQuery.data && rows.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-4">
          <UzCard className="min-w-[220px]">
            <p className="text-xs font-medium text-zinc-500">Total planned</p>
            <p className="mt-1 text-lg font-semibold text-zinc-900 tabular-nums">
              {formatInrPaise(scheduleQuery.data.totalPlannedPaise)}
            </p>
          </UzCard>
          <UzCard className="min-w-[220px]">
            <p className="text-xs font-medium text-zinc-500">Total outstanding</p>
            <p className="mt-1 text-lg font-semibold text-red-600 tabular-nums">
              {formatInrPaise(scheduleQuery.data.totalOutstandingPaise)}
            </p>
          </UzCard>
        </div>
      )}
    </PageShell>
  );
};
